import { useMemo } from 'react';
import parse from 'html-react-parser';
import { useAppSelector } from '@/redux/hooks';
import { profileSelectors } from '@/redux/selectors';
import { BonusTypeEnum } from '@/api';
import { useTranslation } from 'react-i18next';
import { AlertBox, Button } from '@/components';
import { useModal } from '@/hooks';
import { VALID_PERPETUAL } from '@/config';
import { getFormattedDate } from '@/helpers';
import { MonitoringSettingsModal } from '../MonitoringSettingsModal';

import styles from './Preferences.module.sass';

const Preferences = () => {
  const { t } = useTranslation();
  const { data: profileData } = useAppSelector(
    profileSelectors.getProfileSlice,
  );

  const monitoringRiskThreshold = useAppSelector(
    profileSelectors.monitoringRiskThreshold,
  );

  const monitoringBonuses = profileData?.bonuses.filter(
    (bonus) => bonus.type === BonusTypeEnum.TYPE_MONITORING,
  );

  const bonusGroups = useMemo(() => {
    if (!monitoringBonuses) {
      return {};
    }
    const validUntils: Record<string, number> = {};

    monitoringBonuses.forEach((item) => {
      const untilDate = item.valid_until ?? VALID_PERPETUAL;
      validUntils[untilDate] =
        (validUntils[untilDate] ?? 0) + Number(item.value);
    });

    return validUntils;
  }, [profileData?.bonuses]);

  const {
    isOpen: isOpenPreferencesModal,
    onOpenModal: handleOpenModal,
    onCloseModal: handleCloseModal,
  } = useModal();

  return (
    <>
      <div className={styles.preferences}>
        {!!Object.keys(bonusGroups).length && (
          <>
            {Object.keys(bonusGroups).map((validUntil: string) => (
              <div key={validUntil} className={styles.bonus}>
                {VALID_PERPETUAL !== validUntil
                  ? parse(
                      t('translation.bonus_monitoring_description', {
                        count: Number(bonusGroups[validUntil]),
                        date: getFormattedDate(validUntil),
                      }),
                    )
                  : parse(
                      t('translation.bonus_monitoring_short_description', {
                        count: Number(bonusGroups[validUntil]),
                      }),
                    )}
              </div>
            ))}
          </>
        )}

        <div className={styles.inner}>
          <AlertBox type="warning">
            {{
              description: parse(
                t('translation.subscribe_description', {
                  threshold: monitoringRiskThreshold,
                }),
              ),
            }}
          </AlertBox>

          <Button
            type="secondary"
            corner="rect"
            className={styles.actionButton}
            onClick={handleOpenModal}
          >
            {t('translation.monitoring_preference_btn')}
          </Button>
        </div>
      </div>

      <MonitoringSettingsModal
        isOpen={isOpenPreferencesModal}
        onClose={handleCloseModal}
      />
    </>
  );
};

export default Preferences;
